import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { FilledInput, Button, FormGroup, FormLabel, FormControl, Box, Grid, Divider, Tooltip, Icon, IconButton } from '@material-ui/core';
import { useParams, useHistory, Link } from 'react-router-dom';
import { DataGrid } from '@material-ui/data-grid';
import dayjs from 'dayjs';

import Loader from '../../common/loader';
import NotFound from '../../common/not-found';
import TicketMasterDetailCreateBreakDown from '../components/ticket-master-detail-create-break-down';
import TicketMasterDetailEdit from '../components/ticket-master-detail-edit';

import AuthContext from '../../context';
import { get, del, post } from '../../utils/api-services';
import helpers from '../../utils/helpers';
import themeColors from '../../assets/colors';

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    divider: {
        marginBottom: 24
    },
    highlightedNumber: {
        color: '#0d6efd'
    },
    controlsContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    buttonsContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: 24
    },
    createBreakDownBtn: {
        ...themeColors.greenBtn
    },
    deleteBtn: {
        marginRight: 15,
        ...themeColors.redBtn
    },
    editBtn: {
        marginRight: 15,
        ...themeColors.yellowBtn
    },
    generateCashPaymentBtn: {
        marginRight: 15,
        ...themeColors.greenBtn
    },
    tableContainer: {
        width: '100%',
        marginBottom: 20,
        '& .total': {
            backgroundColor: '#E0F8E0'
        }
    }
}));

const TicketMasterDetail = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const sede = JSON.parse(localStorage.getItem('@sede'));
    const { id } = useParams();
    const classes = useStyles();
    const history = useHistory();
    const [showCreateBreakDown, setShowCreateBreakDown] = useState(false);
    const [showEditMaster, setShowEditMaster] = useState(false);
    const [state, setState] = useState({
        loading: true,
        master: {
            idticket_master: 0,
            code: '',
            date: '',
            total: 0,
            total_qty: 0,
            cash_payment: false,
            is_automatic: true,
            net_weight_scale: 0,
            net_weight_software: 0,
            purchase_order: '',
            supplier: {},
            land: {},
            location: {},
            carrier: {},
            tickets: []
        },
        permissions: {}
    });

    useEffect(() => {
        window.document.title = 'Master ticket - detail';

        makeRequest();
    }, []);

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get(`/ticketMaster/${id}`);
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                master: response.data
            }));
        } else {
            console.log('[TicketMasterDetail] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const validate = () => {
        const { idticket_master, tickets } = state.master;
        if (!Number(idticket_master)) {
            showToast('Please try again later', 'error');
            return false;
        }
        if (tickets.length > 0) {
            showToast('You cannot delete this master if there is breakdowns assigned', 'error', 7000);
            return false;
        }

        return window.confirm('Are you sure you want to delete this master ticket?');
    }

    const destroy = async () => {
        const cond = validate();
        if (cond) {
            showLoader(true);
            const response = await del(`/ticketMaster/${id}`);
            if (response.status === 200) {
                history.replace('/ticket/tickets');
                showToast(response.message);
            } else {
                console.log('[TicketMasterDetail] destroy error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const toggleShow = form => {
        if (form === 'create') {
            setShowEditMaster(false);
            setShowCreateBreakDown(!showCreateBreakDown);
        }
        if (form === 'edit') {
            setShowCreateBreakDown(false);
            setShowEditMaster(!showEditMaster);
        }
    }

    const generateCashPayment = async () => {
        const cond = window.confirm('Are you sure you want to generate cash payment for this master ticket?');
        if (cond) {
            showLoader(true);
            const response = await post(`/check/storeCashPayment`, { idticket_master: id });
            if (response.status === 200) {
                history.push(`/ticket/checks/${response.id}`);
                showToast(response.message);
            } else {
                console.log('[TicketMasterDetail] generateCashPayment error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const getProduct = ({ row }) => row.product_type_ref?.product?.name ?? 'N/A';
    const getType = ({ row }) => row.product_type_ref?.type?.name ?? 'N/A';
    const getMoney = ({ value }) => `$ ${value}`;
    const renderCell = ({ row }) => {
        return (
            <Tooltip title='Show detail' placement='bottom'>
                <Link to={`/ticket/tickets/${row.idticket}`} target='_blank'>
                    <IconButton>
                        <Icon>{'visibility'}</Icon>
                    </IconButton>
                </Link>
            </Tooltip>
        );
    }

    const columns = [
        { field: 'code', headerName: 'Code', flex: 0.7, minWidth: 98 },
        { field: 'state', headerName: 'Status', flex: 0.5, minWidth: 70 },
        { field: 'product', headerName: 'Product', flex: 1, minWidth: 140, valueGetter: getProduct },
        { field: 'type', headerName: 'Type', flex: 0.7, minWidth: 98, valueGetter: getType },
        { field: 'price', headerName: 'Price', flex: 0.7, minWidth: 98, valueGetter: getMoney },
        { field: 'total_processing_qty', headerName: 'Qty', flex: 0.7, minWidth: 98 },
        { field: 'total', headerName: 'Total', flex: 0.8, minWidth: 112, valueGetter: getMoney },
        { field: 'detail', headerName: 'Detail', flex: 0.5, minWidth: 70, renderCell }
    ]

    if (state.loading) return <Loader />;
    if (!state.permissions.read_attr || !state.master?.idticket_master) return <NotFound />

    return (
        <div className={classes.root}>
            <h2>{'Master'}</h2>
            <Grid container>
                <Grid item xs={6} lg={3} xl={1}>
                    <FormGroup>
                        <FormLabel>{'Code'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='Code'
                                value={state.master.code}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={3} xl={1}>
                    <FormGroup>
                        <FormLabel>{'Date'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='Date'
                                value={dayjs(state.master.date).format('MM/DD/YYYY')}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={3} xl={2}>
                    <FormGroup>
                        <FormLabel>{'Supplier'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='Supplier'
                                value={state.master.supplier?.name ?? ''}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={3} xl={2}>
                    <FormGroup>
                        <FormLabel>{'Land'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='Land'
                                value={state.master.land?.name ?? ''}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={3} xl={2}>
                    <FormGroup>
                        <FormLabel>{'Location'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='Location'
                                value={state.master.location?.name ?? ''}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3} xl={2}>
                    <FormGroup>
                        <FormLabel>{'Sede'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='Sede'
                                value={sede.name}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={3} xl={2}>
                    <FormGroup>
                        <FormLabel>{'Payment method'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='Payment'
                                value={state.master.cash_payment ? 'Cash' : 'Check'}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>

            <Grid container>
                {
                    state.master.purchase_order &&
                    <Grid item xs={6} lg={3} xl={2}>
                        <FormGroup>
                            <FormLabel>{'Purchase order'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    readOnly
                                    placeholder='PO no.'
                                    value={state.master.purchase_order}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                }
                <Grid item xs={6} lg={3} xl={1}>
                    <FormGroup>
                        <FormLabel>{'Total $'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                placeholder='Total'
                                value={`$ ${state.master?.total ?? 0}`}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3} xl={1}>
                    <FormGroup>
                        <FormLabel>{'Total qty'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                placeholder='Total qty'
                                value={state.master.total_qty}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                {
                    (!state.master.is_automatic) &&
                    <>
                        <Grid item xs={6} lg={3} xl={2}>
                            <FormGroup>
                                <FormLabel>{'Net weight scale'}</FormLabel>
                                <FormControl
                                    variant='filled'
                                    component={Box}
                                    width='100%'
                                >
                                    <FilledInput
                                        readOnly
                                        placeholder='Weight scale'
                                        value={state.master.net_weight_scale}
                                    />
                                </FormControl>
                            </FormGroup>
                        </Grid>
                        <Grid item xs={6} lg={3} xl={2}>
                            <FormGroup>
                                <FormLabel>{'Net weight software'}</FormLabel>
                                <FormControl
                                    variant='filled'
                                    component={Box}
                                    width='100%'
                                >
                                    <FilledInput
                                        readOnly
                                        placeholder='Weight software'
                                        value={state.master.net_weight_software}
                                    />
                                </FormControl>
                            </FormGroup>
                        </Grid>
                        <Grid item xs={6} lg={3} xl={2}>
                            <FormGroup>
                                <FormLabel>{'Negative product'}</FormLabel>
                                <FormControl
                                    variant='filled'
                                    component={Box}
                                    width='100%'
                                >
                                    <FilledInput
                                        readOnly
                                        placeholder='Negative'
                                        value={state.master.net_weight_software - state.master.total_qty}
                                    />
                                </FormControl>
                            </FormGroup>
                        </Grid>
                    </>
                }
                {
                    (state.master.carrier?.id) &&
                    <Grid item xs={12} lg={3} xl={2}>
                        <FormGroup>
                            <FormLabel>{'Carrier'}</FormLabel>
                            <Link to={`/ticket/ticketCarrier/${state.master.carrier?.id}`}>
                                <FormControl
                                    variant='filled'
                                    component={Box}
                                    width='100%'
                                >
                                    <FilledInput
                                        readOnly
                                        inputProps={{ style: { cursor: 'pointer' } }}
                                        placeholder='Carrier'
                                        value={state.master.carrier?.name ?? ''}
                                    />
                                </FormControl>
                            </Link>
                        </FormGroup>
                    </Grid>
                }
            </Grid>

            <div className={classes.buttonsContainer}>
                {
                    (state.permissions?.delete_attr && state.master.tickets.length < 1) &&
                    <Button
                        variant='contained'
                        className={classes.deleteBtn}
                        onClick={destroy}
                    >
                        {'Delete'}
                    </Button>
                }
                {
                    (state.permissions?.update_attr && state.master.tickets.length < 1) &&
                    <Button
                        variant='contained'
                        className={classes.editBtn}
                        onClick={() => toggleShow('edit')}
                    >
                        {'Edit'}
                    </Button>
                }
                {
                    (state.permissions?.update_attr && state.master.tickets.length >= 1 && state.master.cash_payment) &&
                    <Button
                        variant='contained'
                        className={classes.generateCashPaymentBtn}
                        onClick={generateCashPayment}
                    >
                        {'Generate cash payment'}
                    </Button>
                }
                <Button
                    variant='contained'
                    className={classes.createBreakDownBtn}
                    onClick={() => toggleShow('create')}
                >
                    {'Create breakdown'}
                </Button>
            </div>

            <TicketMasterDetailCreateBreakDown
                open={showCreateBreakDown}
                onClose={() => setShowCreateBreakDown(false)}
                id={id}
                permissions={state.permissions}
                setMaster={master => setState(prev => ({ ...prev, master }))}
            />

            <TicketMasterDetailEdit
                open={showEditMaster}
                onClose={() => setShowEditMaster(false)}
                id={id}
                permissions={state.permissions}
                master={state.master}
                setMaster={master => setState(prev => ({ ...prev, master }))}
            />

            <Divider className={classes.divider} />

            <h3>{'Breakdowns'}</h3>
            <div className={classes.tableContainer}>
                <DataGrid
                    autoHeight
                    pageSize={30}
                    density='compact'
                    columns={columns}
                    rows={state.master.tickets}
                    getRowId={row => row.idticket}
                    hideFooter={state.master.tickets?.length <= 30}
                />
            </div>
        </div>
    );
}

export default TicketMasterDetail;